import { useState } from "react";
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useTranslate,
  type DataProvider,
} from "ra-core";
import { Download, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

import type { Company, Contact } from "../types";
import type { Shipment } from "./types";

// Blob URLs stay valid while the preview tab may still be loading them.
const REVOKE_AFTER_MS = 60_000;

// The PDF renderer is ~1 MB, so it is only loaded when someone actually opens an invoice.
export const renderInvoicePdf = async (
  shipment: Shipment,
  company: Company,
  contact: Contact | null,
): Promise<{ blob: Blob; fileName: string }> => {
  const [{ pdf }, { InvoiceDocument, registerInvoiceFonts }] =
    await Promise.all([
      import("@react-pdf/renderer"),
      import("./InvoiceDocument"),
    ]);
  registerInvoiceFonts(`${window.location.origin}/fonts`);
  const blob = await pdf(
    <InvoiceDocument shipment={shipment} company={company} contact={contact} />,
  ).toBlob();
  const signed = shipment.signed_at ? "_signed" : "";
  const fileName = `Invoice_${shipment.invoice_number}_${company.name.replace(/[^A-Za-z0-9]/g, "")}${signed}.pdf`;
  return { blob, fileName };
};

const buildInvoice = async (
  shipment: Shipment,
  dataProvider: DataProvider,
): Promise<{ blob: Blob; fileName: string }> => {
  const { data: company } = await dataProvider.getOne<Company>("companies", {
    id: shipment.company_id,
  });
  const contact = shipment.contact_id
    ? (
        await dataProvider.getOne<Contact>("contacts", {
          id: shipment.contact_id,
        })
      ).data
    : null;
  return renderInvoicePdf(shipment, company, contact);
};

export const saveBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
};

type Mode = "download" | "preview";

const InvoiceButton = ({
  mode,
  size = "sm",
}: {
  mode: Mode;
  size?: "sm" | "default";
}) => {
  const shipment = useRecordContext<Shipment>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const [busy, setBusy] = useState(false);
  if (!shipment) return null;

  const run = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open the tab inside the click handler, before any await, or popup blockers stop it.
    const tab = mode === "preview" ? window.open("", "_blank") : null;
    setBusy(true);
    try {
      const { blob, fileName } = await buildInvoice(shipment, dataProvider);
      if (mode === "preview") {
        const url = URL.createObjectURL(blob);
        if (tab) tab.location.href = url;
        else window.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
      } else {
        saveBlob(blob, fileName);
      }
    } catch (error: unknown) {
      tab?.close();
      notify(
        translate("egi.invoice.failed", {
          error: error instanceof Error ? error.message : "unknown error",
        }),
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  const Icon = mode === "preview" ? Eye : Download;
  const label =
    mode === "preview" ? "egi.invoice.preview" : "egi.invoice.download";
  return (
    <Button size={size} variant="outline" onClick={run} disabled={busy}>
      <Icon className="size-4" />
      {translate(busy ? "egi.invoice.preparing" : label)}
    </Button>
  );
};

export const DownloadInvoiceButton = ({
  size,
}: {
  size?: "sm" | "default";
}) => <InvoiceButton mode="download" size={size} />;

export const PreviewInvoiceButton = ({ size }: { size?: "sm" | "default" }) => (
  <InvoiceButton mode="preview" size={size} />
);
